import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Camera, Save, CalendarClock, Beer, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError, brl, userPhotoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { prettyDate } from "@/lib/dates";
import FloatingMenuButton from "@/components/FloatingMenuButton";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", instagram: "" });
  const [reservations, setReservations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", phone: user.phone || "", instagram: user.instagram || "" });
  }, [user]);

  useEffect(() => {
    api.get("/me/reservations").then((r) => setReservations(r.data)).catch(() => {});
    api.get("/me/orders").then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me", form);
      await refresh();
      toast.success("Perfil atualizado!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/me/photo", fd);
      await refresh();
      toast.success("Foto atualizada!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const pending = user && !user.approved && user.role !== "admin";

  return (
    <div className="min-h-screen bg-[#FBFBFB]" data-testid="profile-page">
      <header className="relative overflow-hidden bg-[#0b1610]">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b1610] via-[#123021] to-[#0b1610]" />
        <div className="relative mx-auto max-w-3xl px-5 py-10">
          <Link to="/" data-testid="profile-back" className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/90 backdrop-blur transition hover:bg-white/20">
            <ArrowLeft className="h-3.5 w-3.5" /> Menu
          </Link>
          <div className="mt-6 flex items-center gap-4">
            <div className="relative">
              {userPhotoUrl(user) ? (
                <img src={userPhotoUrl(user)} alt="" className="h-20 w-20 rounded-full border-2 border-white/30 object-cover" />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white/20 text-2xl font-black text-white">{(user?.name || "?")[0]}</div>
              )}
              <button data-testid="profile-photo-btn" onClick={() => fileRef.current?.click()} className="absolute -bottom-1 -right-1 rounded-full bg-[#E8611A] p-2 text-white shadow-lg hover:bg-[#CF5212]"><Camera className="h-4 w-4" /></button>
              <input ref={fileRef} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="profile-photo-input" />
            </div>
            <div>
              <p className="font-display text-2xl font-black text-white">{user?.name}</p>
              <p className="text-sm text-emerald-100/70">{user?.email}</p>
              {pending && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2 py-0.5 text-xs font-bold text-amber-200"><Clock className="h-3 w-3" /> Aguardando aprovação</span>}
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-8">
        {/* Edit form */}
        <div className="rounded-3xl border border-slate-200 bg-white p-5">
          <h2 className="font-display text-lg font-bold text-slate-900">Meus dados</h2>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div><label className="text-xs font-semibold text-slate-500">Nome</label><Input data-testid="profile-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><label className="text-xs font-semibold text-slate-500">WhatsApp</label><Input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 rounded-xl" /></div>
            <div><label className="text-xs font-semibold text-slate-500">Instagram</label><Input data-testid="profile-instagram" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="mt-1 rounded-xl" /></div>
          </div>
          <Button data-testid="profile-save" onClick={save} disabled={saving} className="mt-4 rounded-full bg-[#1B7A45] text-white hover:bg-[#156337]"><Save className="mr-1.5 h-4 w-4" /> {saving ? "Salvando..." : "Salvar"}</Button>
        </div>

        {/* Reservations */}
        <div className="mt-6">
          <div className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-[#1B7A45]" /><h2 className="font-display text-lg font-bold text-slate-900">Minhas reservas</h2></div>
          <div className="mt-3 space-y-2">
            {reservations.length === 0 ? <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-6 text-center text-sm text-slate-400">Nenhuma reserva ainda.</p> :
              reservations.map((r) => (
                <div key={r.id} data-testid={`profile-res-${r.id}`} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-3">
                  <div><p className="font-bold text-slate-900">{r.space_name}</p><p className="text-xs text-slate-500">{prettyDate(r.date)} · {r.time_label}</p></div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${r.status === "confirmada" ? "bg-emerald-100 text-emerald-700" : r.status === "cancelada" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Orders */}
        <div className="mt-6">
          <div className="flex items-center gap-2"><Beer className="h-5 w-5 text-[#C7962B]" /><h2 className="font-display text-lg font-bold text-slate-900">Minhas compras no bar</h2></div>
          <div className="mt-3 space-y-2">
            {orders.length === 0 ? <p className="rounded-2xl border border-dashed border-slate-300 bg-white py-6 text-center text-sm text-slate-400">Nenhuma compra ainda.</p> :
              orders.map((o) => (
                <div key={o.id} data-testid={`profile-order-${o.id}`} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white p-3">
                  <div><p className="text-sm text-slate-700">{o.items.map((i) => `${i.qty}x ${i.name}`).join(", ")}</p><p className="font-bold text-[#1B7A45]">{brl(o.total)}</p></div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${o.status === "confirmada" ? "bg-emerald-100 text-emerald-700" : o.status === "cancelada" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{o.status}</span>
                </div>
              ))}
          </div>
        </div>
      </main>

      <FloatingMenuButton />
    </div>
  );
}
